import { computed, defineComponent, inject, onBeforeUpdate, onMounted, onUpdated, reactive, ref, watch, } from 'vue';
import CatalogItem from '@/components/CatalogItem.vue';
import FilterList from '@/components/FilterList.vue';
import Dropdown from '@/components/Dropdown.vue';
import Spinner from '@/components/Spinner.vue';
import PriceRangeSlider from '@/components/PriceRangeSlider.vue';
import { CatalogOrder, } from '@/services/api';
import PermalinkList from './PermalinkList.vue';
import { wcserviceClientKey } from '@/main';
import $ from 'jquery';
import { getGtagCallbacks } from '@/gtag.utils';
import { GA4 } from '@/ga4';
export default defineComponent({
    name: 'Catalog',
    props: {
        config: {
            type: Object,
            required: true,
        },
    },
    components: {
        Spinner,
        CatalogItem,
        FilterList,
        PermalinkList,
        Dropdown,
        PriceRangeSlider,
    },
    setup(props) {
        const sidebar = ref(null);
        const sidebarMoved = ref(false);
        const page = ref(0);
        const loadingCatalog = ref(true);
        const loadingProducts = ref(false);
        const loadingMoreProducts = ref(false);
        const products = ref([]);
        const priceRange = ref({ min: 0, max: 0 });
        const selectedPriceRange = ref(null);
        const order = ref(CatalogOrder.Default);
        const sidebarOpen = ref(false);
        const ddSet = ref(new Set());
        const taxRefs = new Map();
        const priceRangeOpen = ref(false);
        for (const options of props.config.taxonomies) {
            taxRefs.set(options.taxonomy, reactive({
                options: options,
                terms: [],
                flatTerms: new Map(),
                selectedTerms: new Set(),
                loading: false,
            }));
            const taxRef = taxRefs.get(options.taxonomy);
            // this is not possible
            if (taxRef === undefined) {
                continue;
            }
            if (taxRef.options.exclude && taxRef.options.exclude.length > 0) {
                taxRef.options.enableFilter = false;
            }
        }
        const wcserviceClient = inject(wcserviceClientKey);
        if (wcserviceClient === undefined) {
            throw new Error('Cannot inject wcserviceClient');
        }
        const ga4Enabled = props.config.ga4.enabled;
        const ga4 = new GA4(props.config.ga4.listId ?? '', props.config.ga4.listName ?? '', props.config.ga4.brandFallback);
        const { gtagAddToCart, gtagSelectContent, gtagViewItemList } = getGtagCallbacks(props.config.gtag);
        const showLoadMore = computed(() => {
            if (props.config.productIds.length > 0) {
                return false;
            }
            return (products.value.length ===
                (props.config.productsPerPage ?? 24) * (page.value + 1));
        });
        const productQuery = () => {
            const query = {
                taxonomies: {},
            };
            if (props.config.productIds.length > 0) {
                query.ids = props.config.productIds;
            }
            if (selectedPriceRange.value !== null) {
                query.minPrice = selectedPriceRange.value.min;
                query.maxPrice = selectedPriceRange.value.max;
            }
            if (props.config.searchString !== undefined) {
                query.title = props.config.searchString;
                query.searchLogic = 'or';
            }
            for (const [tax, taxRef] of taxRefs.entries()) {
                const filter = { op: 'or', terms: [] };
                if (taxRef.options.exclude && taxRef.options.exclude.length > 0) {
                    filter.op = 'not';
                    filter.terms = taxRef.options.exclude;
                    query.taxonomies[tax] = filter;
                    continue;
                }
                if (taxRef.options.selectedParent) {
                    filter.terms.push(taxRef.options.selectedParent);
                }
                // excluding parent terms from query
                const termsToExclude = [];
                for (const t of taxRef.selectedTerms.values()) {
                    const term = taxRef.flatTerms.get(t);
                    // this should never happens
                    if (term === undefined) {
                        continue;
                    }
                    filter.terms.push(term.id);
                    if (term.parent !== '0') {
                        termsToExclude.push(term.parent);
                    }
                }
                filter.terms = filter.terms.filter(t => !termsToExclude.includes(t));
                if (filter.terms.length > 0) {
                    query.taxonomies[tax] = filter;
                }
            }
            return query;
        };
        const catalogQuery = () => {
            const limit = props.config.productsPerPage;
            return {
                limit: limit,
                offset: limit * page.value,
                query: productQuery(),
                order: order.value,
                postMetaIn: ['_attribute_list', '_sku', '_wc_average_rating'],
                taxonomiesIn: [
                    'product_cat',
                    'product_type',
                    'product_brand',
                    'selection_taxonomy',
                ],
                termMetaIn: ['selection_badge', 'taxdetails_0_taxdetails_badge', 'taxdetails_0_taxdetails_start_date', 'taxdetails_0_taxdetails_end_date'],
            };
        };
        const loadTaxonomy = async (tax, omitSelf = false) => {
            const taxRef = taxRefs.get(tax);
            if (taxRef === undefined) {
                console.warn(`Taxonomy reload failed: taxonomy \`${tax}\` does not exists`);
                return;
            }
            if (taxRef.options.enableFilter === false) {
                return;
            }
            taxRef.loading = true;
            const query = productQuery();
            if (omitSelf && query.taxonomies !== undefined) {
                delete query.taxonomies[tax];
            }
            taxRef.terms = await wcserviceClient.findTaxonomyTerms(tax, {
                productQuery: query,
                parent: taxRef.options.selectedParent,
            });
            // populate flat term map
            const populateFlatTermMap = (terms) => {
                for (const t of terms) {
                    taxRef.flatTerms.set(t.id, t);
                    if (t.children.length > 0) {
                        populateFlatTermMap(t.children);
                    }
                }
            };
            populateFlatTermMap(taxRef.terms);
            taxRef.loading = false;
        };
        const loadAllTaxonomies = async (omit = [], omitSelf = false) => {
            const promises = [];
            for (const [tax, ref] of taxRefs.entries()) {
                if (omit.includes(tax)) {
                    continue;
                }
                promises.push(loadTaxonomy(tax, omitSelf));
            }
            await Promise.all(promises);
        };
        const loadProducts = async () => {
            loadingProducts.value = true;
            page.value = 0;
            products.value = await wcserviceClient.findProducts(catalogQuery());
            gtagViewItemList(products.value, 0);
            if (ga4Enabled) {
                ga4.viewItemList(products.value, 0);
            }
            loadingProducts.value = false;
        };
        const loadPriceRange = async () => {
            const res = await wcserviceClient.getPriceRange(catalogQuery());
            priceRange.value.min = Math.floor(res.min);
            priceRange.value.max = Math.ceil(res.max);
            if (selectedPriceRange.value === null) {
                selectedPriceRange.value = {
                    min: priceRange.value.min,
                    max: priceRange.value.max,
                };
            }
        };
        const loadMoreProducts = async () => {
            loadingMoreProducts.value = true;
            page.value++;
            const newProducts = await wcserviceClient.findProducts(catalogQuery());
            gtagViewItemList(newProducts, products.value.length);
            if (ga4Enabled) {
                ga4.viewItemList(newProducts, products.value.length);
            }
            products.value = products.value.concat(newProducts);
            loadingMoreProducts.value = false;
        };
        const resetCatalogScroll = () => {
            const catalog = document.querySelector('.main__grid');
            const html = document.querySelector('html');
            if (html === null || catalog === null) {
                return;
            }
            html.scrollTop = catalog.offsetTop;
        };
        const reloadCatalog = async (resetScroll = false) => {
            await loadPriceRange();
            await Promise.all([loadAllTaxonomies([], true), loadProducts()]);
            if (resetScroll) {
                resetCatalogScroll();
            }
        };
        const checkCallback = (tax, term, checked) => {
            const taxRef = taxRefs.get(tax);
            if (taxRef === undefined) {
                console.warn(`Taxonomy reload failed: taxonomy \`${tax}\` does not exists`);
                return;
            }
            if (checked) {
                taxRef.selectedTerms.add(term.id);
            }
            else {
                taxRef.selectedTerms.delete(term.id);
                // uncheck recursively its own children
                const uncheckChildren = (term) => {
                    for (const c of term.children) {
                        taxRef.selectedTerms.delete(c.id);
                        if (c.children.length > 0) {
                            uncheckChildren(c);
                        }
                    }
                };
                uncheckChildren(term);
            }
        };
        const checkAndReloadCallback = async (tax, term, checked) => {
            checkCallback(tax, term, checked);
            reloadCatalog();
        };
        const applyCallback = async (tax, checkedTerms) => {
            const taxRef = taxRefs.get(tax);
            if (taxRef === undefined) {
                console.warn(`Taxonomy reload failed: taxonomy \`${tax}\` does not exists`);
                return;
            }
            taxRef.selectedTerms = checkedTerms;
            await loadPriceRange();
            await Promise.all([loadAllTaxonomies([], true), loadProducts()]);
        };
        const onPriceRangeSliderChange = (values, reload = false) => {
            selectedPriceRange.value = {
                min: values[0],
                max: values[1],
            };
            if (reload) {
                Promise.all([loadAllTaxonomies(), loadProducts()]);
            }
        };
        const priceRangeSliderChangeAndReload = (values) => {
            onPriceRangeSliderChange(values, true);
        };
        const onPriceRangeSliderApply = (e) => {
            onDdToggle(e, false);
            Promise.all([loadAllTaxonomies(), loadProducts()]);
            sidebarOpen.value = false;
        };
        const addDdRef = (el) => {
            ddSet.value.add(el);
        };
        const onDdToggle = (e, toggle) => {
            const $dd = $(e.target)
                .parent('.dropdown')
                .find('.dropdown__content');
            const previousState = $dd.css('display');
            for (const el of ddSet.value.values()) {
                $(el.$el).find('.dropdown__content').css('display', 'none');
            }
            if (toggle !== undefined) {
                $dd.css('display', toggle ? 'block' : 'none');
            }
            else {
                $dd.css('display', previousState === 'block' ? 'none' : 'block');
            }
        };
        const toggleAndReload = async (e) => {
            onDdToggle(e);
            reloadCatalog();
            sidebarOpen.value = false;
        };
        const addToCart = (product, index) => {
            gtagAddToCart(product, index);
            if (ga4Enabled) {
                ga4.addToCart(product, index);
            }
        };
        const viewDetails = (product, index) => {
            gtagSelectContent(product, index);
            if (ga4Enabled) {
                ga4.selectItem(product, index);
            }
        };
        const resetFilters = async () => {
            taxRefs.forEach(v => {
                v.selectedTerms.clear();
            });
            order.value = CatalogOrder.Default;
            selectedPriceRange.value = null;
            await reloadCatalog(true);
        };
        watch(order, (newVal, oldVal) => {
            loadProducts();
        });
        onMounted(async () => {
            loadingCatalog.value = true;
            await loadPriceRange();
            await Promise.all([loadAllTaxonomies(), loadProducts()]);
            let reload = false;
            for (const [tax, ref] of taxRefs.entries()) {
                if (ref.options.selectedTerms === undefined) {
                    continue;
                }
                for (const tId of ref.options.selectedTerms) {
                    const term = ref.flatTerms.get(tId);
                    if (term === undefined) {
                        continue;
                    }
                    checkCallback(tax, term, true);
                    reload = true;
                }
            }
            if (reload) {
                await reloadCatalog();
            }
            loadingCatalog.value = false;
        });
        onUpdated(() => {
            if (sidebarMoved.value) {
                return;
            }
            const tp = props.config.teleportSidebar;
            if (tp === undefined) {
                return;
            }
            const destination = document.querySelector(tp);
            if (destination === null) {
                console.warn(`Teleport destination element ${tp} does not exists. Skipping`);
                return;
            }
            if (sidebar.value !== null) {
                destination.appendChild(sidebar.value);
                sidebarMoved.value = true;
            }
        });
        onBeforeUpdate(() => {
            ddSet.value = new Set();
        });
        return {
            sidebar,
            sidebarMoved,
            page,
            loadingCatalog,
            loadingProducts,
            loadingMoreProducts,
            products,
            priceRange,
            priceRangeOpen,
            selectedPriceRange,
            order,
            sidebarOpen,
            showLoadMore,
            productQuery,
            catalogQuery,
            taxRefs,
            loadTaxonomy,
            loadAllTaxonomies,
            loadProducts,
            loadMoreProducts,
            loadPriceRange,
            reloadCatalog,
            checkCallback,
            checkAndReloadCallback,
            applyCallback,
            onPriceRangeSliderChange,
            priceRangeSliderChangeAndReload,
            onPriceRangeSliderApply,
            addDdRef,
            onDdToggle,
            toggleAndReload,
            addToCart,
            viewDetails,
            resetFilters,
        };
    },
});
